<template>
  <div class="flex flex-column mb-2">
    <p v-if="configs == null || configs.length == 0">
      Please add a Perk Config
    </p>
    <div style="padding-left: 12px">
      <div
        v-for="(perkConfig, index) in configs"
        :key="index"
        class="field border-1 border-gray-300 p-2 border-round shadow-1"
      >
        <label
          :for="'config-' + index"
          class="font-bold text-900"
          style="font-size: 90%; margin-bottom: 2px"
          >Perk {{ index + 1 }}</label
        >
        <div class="p-inputgroup">
          <Dropdown
            v-model="perkConfig.perkId.id"
            :options="perks"
            optionLabel="title"
            optionValue="id"
            :optionDisabled="(perk) => !perk.enabled"
            placeholder="Select Perk"
          />
          <Button
            @click="deleteConfig(index)"
            icon="pi pi-times"
            class="p-button-warning"
          />
        </div>

        <label
          v-if="!perkConfig.isFromNightPay"
          style="font-size: 90%; margin-bottom: 2px"
          class="mt-2"
          >Venue</label
        >
        <div v-if="!perkConfig.isFromNightPay" class="p-inputgroup">
          <Dropdown
            v-model="perkConfig.venueId.id"
            :options="venues"
            optionLabel="name"
            optionValue="id"
            placeholder="All"
          />
        </div>

        <label style="font-size: 90%; margin-bottom: 2px" class="mt-2"
          >Is from NightPay</label
        >
        <div class="p-inputgroup">
          <InputSwitch v-model="perkConfig.isFromNightPay" />
        </div>

        <div>
          <div class="mt-1">
            <label style="font-size: 90%; margin-bottom: 2px" class="mt-2"
              >Perk expires at date</label
            >
            <div>
              <Calendar
                :showIcon="true"
                :modelValue="new Date(perkConfig.expireDate)"
                @update:modelValue="(value) => updateExpireDate(index, value)"
                placeholder="Select date"
                showTime
                hourFormat="24"
                dateFormat="yy-mm-dd"
              />
            </div>
          </div>
        </div>

        <!-- Time-based Redemption Controls -->
        <div class="mt-4 p-3 border-1 border-gray-200 border-round">
          <h4 class="text-sm font-semibold mb-2">🕒 Redemption Time Control</h4>
          
          <div class="field mb-3">
            <label class="text-sm font-medium">Limit perk redemption hours</label>
            <div class="flex align-items-center mt-1">
              <InputSwitch 
                v-model="perkConfig.perkRedemptionTimeControl.isRedemptionTimeLimited" 
                @change="onRedemptionTimeLimitedChange(perkConfig)"
              />
              <span class="ml-2 text-xs text-gray-600">
                {{ perkConfig.perkRedemptionTimeControl.isRedemptionTimeLimited ? 'Enabled' : 'Disabled' }}
              </span>
            </div>
            <small class="text-xs text-gray-500 mt-1">
              When enabled, perk redemption will be restricted to specific hours
            </small>
          </div>

          <div v-if="perkConfig.perkRedemptionTimeControl.isRedemptionTimeLimited" class="grid">
            <div class="col-12 md:col-6">
              <label class="text-sm font-medium">🔒 Locked from time</label>
              <Calendar
                v-model="perkConfig.perkRedemptionTimeControl.lockedFromTimeDate"
                timeOnly
                hourFormat="24"
                placeholder="e.g., 22:00"
                @date-select="onTimeChange(perkConfig, 'lockedFromTime', $event)"
                class="w-full mt-1"
                :inputStyle="{ cursor: 'pointer' }"
                @beforeinput.prevent
                @input.prevent
                @keypress.prevent
                @keydown.prevent
              />
              <small class="text-xs text-gray-500">
                Time when perk becomes unavailable for redemption in {{ detectedTimeZone }}
              </small>
            </div>

            <div class="col-12 md:col-6">
              <label class="text-sm font-medium">🔓 Unlocked at time</label>
              <Calendar
                v-model="perkConfig.perkRedemptionTimeControl.unlockedAtTimeDate"
                timeOnly
                hourFormat="24"
                placeholder="e.g., 10:00"
                @date-select="onTimeChange(perkConfig, 'unlockedAtTime', $event)"
                class="w-full mt-1"
                :inputStyle="{ cursor: 'pointer' }"
                @beforeinput.prevent
                @input.prevent
                @keypress.prevent
                @keydown.prevent
              />
              <small class="text-xs text-gray-500">
                Time when perk becomes available again in {{ detectedTimeZone }}
              </small>
            </div>

            <div class="col-12 mt-2">
              <label class="text-sm font-medium">🌍 Timezone</label>
              <InputText
                v-model="perkConfig.perkRedemptionTimeControl.timeZoneId"
                readonly
                class="w-full mt-1"
              />
              <small class="text-xs text-gray-500">
                Timezone for the redemption time control (auto-detected)
              </small>
            </div>
          </div>
        </div>

        <hr />
        <Button
          @click="assignToExistingMembersNow(perkConfig)"
          icon="pi pi-play"
          label="Assign perk to existing members now"
          class="p-button-warning"
        />
      </div>
      <Button
        @click="addConfig"
        label="Add Perk"
        class="mt-2 p-button-secondary p-button-sm"
      />
    </div>
  </div>
</template>

<script>
import { useConfirm } from "primevue/useconfirm";
import { ref, onMounted, computed } from "vue";
import { useStore } from "vuex";
import { useToast } from "primevue/usetoast";

export default {
  props: ["context", "field"],
  setup(props) {
    const store = useStore();
    const toast = useToast();
    const confirm = useConfirm();

    const generateUUID = () => {
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
        /[xy]/g,
        function (c) {
          var r = (Math.random() * 16) | 0,
            v = c == "x" ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        }
      );
    };

    const loading = ref(true);
    const perks = ref([]);
    const venues = ref([]);
    console.log("props.context", props.context);
    const configs =
      props.context.signUpPerkConfigs != null
        ? ref(props.context.signUpPerkConfigs)
        : ref(props.context);

    const membershipId = ref(props.context.id).value;

    // Get the user's timezone
    const detectedTimeZone = computed(() => {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    });

    const updateExpireDate = (index, value) => {
      configs.value[index].expireDate = value.toISOString();
    };

    function initializeRedemptionTimeControl(config) {
      if (!config.perkRedemptionTimeControl) {
        config.perkRedemptionTimeControl = {
          isRedemptionTimeLimited: false,
          lockedFromTime: null,
          unlockedAtTime: null,
          lockedFromTimeDate: null,
          unlockedAtTimeDate: null,
          timeZoneId: detectedTimeZone.value
        };
      } else {
        // Initialize Date objects for time pickers from string times
        if (config.perkRedemptionTimeControl.lockedFromTime) {
          config.perkRedemptionTimeControl.lockedFromTimeDate = parseTimeToDate(config.perkRedemptionTimeControl.lockedFromTime);
        }
        if (config.perkRedemptionTimeControl.unlockedAtTime) {
          config.perkRedemptionTimeControl.unlockedAtTimeDate = parseTimeToDate(config.perkRedemptionTimeControl.unlockedAtTime);
        }
        // Ensure timezone is set
        if (!config.perkRedemptionTimeControl.timeZoneId) {
          config.perkRedemptionTimeControl.timeZoneId = detectedTimeZone.value;
        }
      }
    }

    function parseTimeToDate(timeString) {
      if (!timeString) return null;
      const [hours, minutes] = timeString.split(':').map(num => parseInt(num, 10));
      
      // Create a date object in local time
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);
      
      return date;
    }

    function formatTimeFromDate(date) {
      if (!date) return null;
      
      // Get time in local timezone
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const seconds = "00"; // Always use 00 for seconds
      
      return `${hours}:${minutes}:${seconds}`; // TimeSpan format for JSON.NET
    }

    function onRedemptionTimeLimitedChange(config) {
      if (!config.perkRedemptionTimeControl.isRedemptionTimeLimited) {
        // Clear time restrictions when disabled
        config.perkRedemptionTimeControl.lockedFromTime = null;
        config.perkRedemptionTimeControl.lockedFromTimeDate = null;
        config.perkRedemptionTimeControl.unlockedAtTime = null;
        config.perkRedemptionTimeControl.unlockedAtTimeDate = null;
      } else {
        // Set default timezone when enabling
        config.perkRedemptionTimeControl.timeZoneId = detectedTimeZone.value;
      }
    }

    function onTimeChange(config, timeType, event) {
      const timeString = formatTimeFromDate(event);
      if (timeType === 'lockedFromTime') {
        config.perkRedemptionTimeControl.lockedFromTime = timeString;
      } else if (timeType === 'unlockedAtTime') {
        config.perkRedemptionTimeControl.unlockedAtTime = timeString;
      }
    }

    async function loadRequiredData() {
      loading.value = true;

      // Load Perks
      try {
        await store.dispatch("perks/ensurePerkListItems");
      } catch (err) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "Could not load Perks: " + err,
          life: 3000,
        });
      }
      const loadedPerks = store.getters["perks/perkListItems"];
      perks.value = loadedPerks;

      loadVenues();
      loading.value = false;
    }

    async function loadVenues() {
      await store.dispatch("venues/ensureVenueListItems");
      const loadedVenues = store.getters["venues/venueListItems"];
      venues.value = [{ id: null, name: "All" }, ...loadedVenues];
    }

    function addConfig() {
      const newConfig = {
        perkId: {
          id: null,
        },
        venueId: {
          id: null,
        },
        perkConfigurationId: generateUUID(),
        quantity: 1,
        occurence: 0,
        occurenceType: 0,
        expires: true,
        expire: 0,
        expireType: 0,
        reassigns: false,
        expireDate: new Date().toISOString(),
        reassignsUntilTime: "20:00",
        perkRedemptionTimeControl: {
          isRedemptionTimeLimited: false,
          lockedFromTime: null,
          unlockedAtTime: null,
          lockedFromTimeDate: null,
          unlockedAtTimeDate: null,
          timeZoneId: detectedTimeZone.value
        }
      };
      configs.value.push(newConfig);
    }

    function deleteConfig(index) {
      configs.value.splice(index, 1);
    }

    function assignToExistingMembersNow(perkConfig) {
      if (perkConfig.perkId.id == null) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "Please select a perk",
          life: 3000,
        });
        return;
      }
      if (
        perkConfig.expireDate == null ||
        perkConfig.expireDate == "" ||
        perkConfig.expireDate == undefined ||
        new Date(perkConfig.expireDate) < new Date()
      ) {
        // Check if expire date is before now

        toast.add({
          severity: "error",
          summary: "Error",
          detail: "Please select an valid expire date",
          life: 3000,
        });
        return;
      }

      confirm.require({
        message:
          "Are you sure you want to assign this perk to existing members? this cannot be undone!",
        header: "Please confirm",
        icon: "pi pi-exclamation-triangle",
        accept: async () => {
          loading.value = true;

          try {
            var res = await store.dispatch(
              "membershipPerks/assignSignUpPerksToMembersNow",
              { membershipId: membershipId, perkConfig }
            );
            toast.add({
              severity: "success",
              summary: "Process started",
              detail: "Assigning perk to " + res + " members",
              life: 8000,
            });
          } catch (err) {
            toast.add({
              severity: "error",
              summary: "Error",
              detail: "Something went wrong " + err,
              life: 3000,
            });
          }

          loading.value = false;
        },
        reject: () => {},
      });
    }

    onMounted(async () => {
      await loadRequiredData();
      // Initialize redemption time control for existing configs
      configs.value.forEach(config => {
        initializeRedemptionTimeControl(config);
      });
    });

    return {
      perks,
      venues,
      configs,
      assignToExistingMembersNow,
      addConfig,
      deleteConfig,
      updateExpireDate,
      onRedemptionTimeLimitedChange,
      onTimeChange,
      detectedTimeZone,
    };
  },
};
</script>

<style scoped>
.grid {
  display: grid;
  gap: 1rem;
}

.col-12 {
  grid-column: span 12;
}

.md\:col-6 {
  grid-column: span 6;
}

@media (max-width: 768px) {
  .md\:col-6 {
    grid-column: span 12;
  }
}
</style>
